import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Server, Plus, Copy, RefreshCw, Cpu, Activity, Send, Network, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InsertWizard from "../components/InsertWizard";

const CLOUDS = [
  { id: "aws", name: "AWS" },
  { id: "azure", name: "Azure" },
  { id: "gcp", name: "GCP" },
  { id: "oracle", name: "Oracle" },
];

// How long since `last_heartbeat` before we mark an appliance "stale". Matches
// the appliance's 30s heartbeat cadence with generous slack for clock drift.
const STALE_AFTER_SEC = 120;

export default function Appliances() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cloud: "aws", region: "us-east-1", ttl_seconds: 1800 });
  const [revealed, setRevealed] = useState(null); // { token, token_hash, cloud, region, expires_at }
  const [insertTarget, setInsertTarget] = useState(null); // appliance whose wizard is open

  const load = useCallback(async () => {
    try { const { data } = await api.get("/appliances"); setItems(Array.isArray(data) ? data : []); }
    catch (err) { console.error("appliances load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mintToken = async () => {
    try {
      const { data } = await api.post("/appliances/tokens", form);
      setRevealed(data);
      setOpen(false);
      toast.success("Enrollment token issued");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Clipboard unavailable"); }
  };

  return (
    <Layout title="Appliances" subtitle="DATA PLANE FLEET">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="appliances-count">
          {items.length} APPLIANCES
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} className="gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> REFRESH
          </Button>
          {user?.role === "admin" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="mint-token-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
                  <Plus className="w-4 h-4" /> DEPLOY APPLIANCE
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-display flex items-center gap-2">
                    <Server className="w-4 h-4 text-cyan-400" /> Issue Enrollment Token
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2">
                  <div>
                    <L>Cloud</L>
                    <select
                      value={form.cloud}
                      onChange={(e) => setForm({ ...form, cloud: e.target.value })}
                      className="w-full h-9 bg-zinc-900 border border-zinc-800 rounded-sm px-2 font-mono text-sm"
                    >
                      {CLOUDS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <L>Region</L>
                    <Input
                      value={form.region}
                      onChange={(e) => setForm({ ...form, region: e.target.value })}
                      className="bg-zinc-900 border-zinc-800 rounded-sm font-mono"
                    />
                  </div>
                  <div className="col-span-2">
                    <L>TTL (seconds, 60–86400)</L>
                    <Input
                      type="number" min="60" max="86400"
                      value={form.ttl_seconds}
                      onChange={(e) => setForm({ ...form, ttl_seconds: parseInt(e.target.value || "0", 10) })}
                      className="bg-zinc-900 border-zinc-800 rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 leading-relaxed">
                  Token is shown once. Embed it in cloud-init userdata as
                  <code className="text-cyan-400"> RUSTWALL_ENROLLMENT_TOKEN</code> when launching the appliance VM.
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={mintToken} data-testid="mint-token-submit" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">ISSUE</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {revealed && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4 mb-4" data-testid="token-reveal">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400 mb-2">
            // ENROLLMENT TOKEN — shown once · {revealed.cloud} / {revealed.region}
          </div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs text-zinc-200 break-all flex-1 bg-zinc-950/60 p-2 rounded-sm border border-zinc-800">
              {revealed.token}
            </code>
            <Button size="sm" variant="ghost" onClick={() => copy(revealed.token)} className="gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copy
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRevealed(null)} className="text-zinc-400">Dismiss</Button>
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mt-2">
            hash <span className="text-zinc-300">{revealed.token_hash}</span> · expires {new Date(revealed.expires_at * 1000).toISOString()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="appliances-list">
        {items.map((a) => {
          const stale = isStale(a.last_heartbeat);
          return (
            <div key={a.appliance_id} data-testid={`appliance-card-${a.appliance_id}`}
                 className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display text-base font-bold tracking-tight flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-cyan-400" /> {a.appliance_id}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                    {a.cloud} · {a.region} · slot {a.slot || "?"}
                  </div>
                </div>
                <Status stale={stale} status={a.status} />
              </div>
              <dl className="grid grid-cols-2 gap-y-1 text-[11px] font-mono mt-2">
                <Field k="appliance_version" v={a.appliance_version} />
                <Field k="kernel" v={a.kernel_version} />
                <Field k="enrolled" v={a.enrolled_at} />
                <Field k="last_heartbeat" v={a.last_heartbeat} />
                <Field k="image_hash" v={a.image_hash} />
              </dl>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
                <Button
                  size="sm" variant="ghost"
                  data-testid={`push-policy-${a.appliance_id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/appliances/${a.appliance_id}/sync`);
                      if (data?.receivers > 0) toast.success(`Snapshot pushed (${data.receivers} listener${data.receivers === 1 ? "" : "s"})`);
                      else toast.message("Snapshot built but no live WebSocket — appliance will pick it up on reconnect.");
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                  }}
                  className="gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" /> Push policy now
                </Button>
                {user?.role === "admin" && (
                  <Button
                    size="sm" variant="ghost"
                    data-testid={`insert-${a.appliance_id}`}
                    onClick={() => setInsertTarget(a)}
                    className="gap-1.5 text-cyan-300 hover:text-cyan-200"
                  >
                    <Network className="w-3.5 h-3.5" /> Insert into network
                  </Button>
                )}
                {user?.role === "admin" && (
                  <Button
                    size="sm" variant="ghost"
                    data-testid={`rotate-mtls-${a.appliance_id}`}
                    onClick={async () => {
                      if (!confirm("Mint a fresh mTLS leaf cert and push it to this appliance? It will reconnect the WS with the new cert.")) return;
                      try {
                        const { data } = await api.post(`/appliances/${a.appliance_id}/mtls/renew`);
                        if (data?.ws_receivers > 0) {
                          toast.success(`Cert rotated · pushed live (${data.ws_receivers} WS listener${data.ws_receivers === 1 ? "" : "s"})`);
                        } else {
                          // No live WS — show the cert so the operator can apply manually.
                          toast.message("Cert rotated · no live WS, copy cert/key from the dialog and apply manually.");
                          alert(`mtls_cert_pem:\n${data.mtls_cert_pem}\n\nmtls_key_pem:\n${data.mtls_key_pem}`);
                        }
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                    }}
                    className="gap-1.5 text-amber-300 hover:text-amber-200"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Rotate mTLS
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="text-zinc-500 font-mono text-sm">
            no appliances enrolled yet. Use DEPLOY APPLIANCE to mint a token, embed it in your VM cloud-init, and launch.
          </div>
        )}
      </div>

      {insertTarget && (
        <InsertWizard appliance={insertTarget} onClose={() => setInsertTarget(null)} />
      )}
    </Layout>
  );
}

function L({ children }) {
  return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>;
}

function Field({ k, v }) {
  return (
    <>
      <dt className="text-zinc-500 uppercase tracking-widest text-[10px]">{k}</dt>
      <dd className="text-zinc-200 whitespace-normal break-words" title={v ?? ""}>{v ?? "—"}</dd>
    </>
  );
}

function Status({ stale, status }) {
  // "healthy" + recent heartbeat = green. Stale heartbeat overrides any
  // self-reported status because the appliance hasn't told us anything in 2min.
  const tone = stale
    ? "border-red-500/50 text-red-400 bg-red-500/10"
    : status === "healthy"
      ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10"
      : "border-zinc-700 text-zinc-300 bg-zinc-800/40";
  const label = stale ? "STALE" : (status || "ENROLLED").toUpperCase();
  return (
    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm flex items-center gap-1 ${tone}`}>
      <Activity className="w-3 h-3" /> {label}
    </span>
  );
}

function isStale(iso) {
  if (!iso) return true;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return true;
  return (Date.now() - t) / 1000 > STALE_AFTER_SEC;
}
